import { useState } from "react";
import { CornerUpLeft, ExternalLink, Phone, Copy, Moon, Sun, FileText, Image, Video } from "lucide-react";

/**
 * Prévia visual inspirada no WhatsApp (não é um controle da Meta).
 * Renderiza exclusivamente texto como nós React, sem interpretação de HTML.
 * Não edita o template aprovado, variáveis ou payload de envio.
 */
export function renderWhatsAppText(text) {
  const source = String(text ?? "");
  // Sintaxe de formatação suportada no texto WhatsApp: *negrito*,
  // _itálico_, ~riscado~, ```monoespaçado```. Preserva quebras de linha.
  const token = /(```[\s\S]+?```|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g;
  const parts = source.split(token);
  return parts.map((part, index) => {
    if (part.startsWith("```") && part.endsWith("```") && part.length > 6)
      return <code key={index} className="rounded bg-black/10 px-0.5 font-mono text-[0.95em]">{part.slice(3, -3)}</code>;
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2)
      return <strong key={index} className="font-bold">{part.slice(1, -1)}</strong>;
    if (part.startsWith("_") && part.endsWith("_") && part.length > 2)
      return <em key={index}>{part.slice(1, -1)}</em>;
    if (part.startsWith("~") && part.endsWith("~") && part.length > 2)
      return <s key={index}>{part.slice(1, -1)}</s>;
    return part;
  });
}

function ButtonIcon({ type }) {
  if (type === "PHONE_NUMBER") return <Phone size={16} strokeWidth={2} aria-hidden="true"/>;
  if (type === "URL") return <ExternalLink size={16} strokeWidth={2} aria-hidden="true"/>;
  if (type === "COPY_CODE") return <Copy size={16} strokeWidth={2} aria-hidden="true"/>;
  return <CornerUpLeft size={16} strokeWidth={2} aria-hidden="true"/>;
}

const mediaFormats = { IMAGE: [Image,"Imagem"], VIDEO: [Video,"Vídeo"], DOCUMENT: [FileText,"Documento"] };

export default function WhatsAppTemplatePreview({
  headerFormat = "NONE", headerText = "", bodyText = "", footerText = "", buttons = [],
  emptyMessage = "Digite o texto para visualizar a mensagem.",
}) {
  const [theme, setTheme] = useState("dark");
  const dark = theme === "dark";
  const mediaInfo = mediaFormats[String(headerFormat).toUpperCase()];
  const MediaIcon = mediaInfo?.[0];
  const hasTextHeader = String(headerFormat).toUpperCase() === "TEXT" && !!String(headerText).trim();
  const palette = dark ? {
    panel: "#0b141a", bubble: "#202c33", foreground: "#e9edef",
    muted: "#8696a0", border: "#34414b", accent: "#8acbc6", media: "#334149",
  } : {
    panel: "#e5ddd5", bubble: "#ffffff", foreground: "#111b21",
    muted: "#667781", border: "#e8eaec", accent: "#008c7a", media: "#f0f2f5",
  };
  return <div className="overflow-hidden rounded-2xl border border-slate-200">
    <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-3 py-2">
      <span className="text-[11px] font-semibold text-slate-600">PRÉVIA NO ESTILO DO WHATSAPP</span>
      <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5" aria-label="Aparência da prévia">
        <button type="button" aria-label="Prévia modo claro" aria-pressed={!dark} onClick={()=>setTheme("light")} className={`flex items-center gap-1 rounded-md px-2 py-1 text-[11px] ${!dark?"bg-slate-200 font-bold text-slate-900":"text-slate-500"}`}><Sun size={13}/>Claro</button>
        <button type="button" aria-label="Prévia modo escuro" aria-pressed={dark} onClick={()=>setTheme("dark")} className={`flex items-center gap-1 rounded-md px-2 py-1 text-[11px] ${dark?"bg-slate-800 font-bold text-white":"text-slate-500"}`}><Moon size={13}/>Escuro</button>
      </div>
    </div>
    <div className="p-3 sm:p-4" style={{background:palette.panel}}>
      <div className="mx-auto w-full max-w-[440px] overflow-hidden rounded-[9px] shadow-sm" style={{background:palette.bubble,color:palette.foreground}}>
        <div className="px-3.5 pb-2.5 pt-3.5 text-[14px] leading-[1.43] sm:text-[15px]">
          {mediaInfo && <div className="mb-3 flex min-h-28 items-center justify-center gap-2 rounded-lg text-sm" style={{background:palette.media,color:palette.muted}}><MediaIcon size={22}/>{mediaInfo[1]} · prévia ilustrativa</div>}
          {hasTextHeader && <div className="mb-2 whitespace-pre-wrap break-words font-bold leading-[1.4]">{renderWhatsAppText(headerText)}</div>}
          <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{renderWhatsAppText(bodyText || emptyMessage)}</div>
          {!!String(footerText).trim() && <p className="mt-2 whitespace-pre-wrap break-words text-[12px]" style={{color:palette.muted}}>{renderWhatsAppText(footerText)}</p>}
        </div>
        {buttons.map((button, index) => <div key={`${index}-${button.type||"QUICK_REPLY"}`} className="flex min-h-[46px] items-center justify-center gap-2 px-3 py-2 text-center text-[14px] font-medium" style={{borderTop:`1px solid ${palette.border}`,color:palette.accent}}><ButtonIcon type={button.type}/><span>{button.type === "COPY_CODE" ? (button.text || "Copiar código") : (button.text || "Botão")}</span></div>)}
      </div>
    </div>
    <div className="bg-white px-3 py-2 text-[11px] text-slate-500">Prévia aproximada. Ícones, cores e disposição final dos botões dependem do WhatsApp e do aparelho. O conteúdo aprovado não é alterado.</div>
  </div>;
}
